// Your account: what you are called, and the password you sign in with; and
// how the viewer loads slides for you (ViewerCard).
//
// It exists because names now do. Signup asks for a first and last name, and a
// person who was created before that — or by an administrator, or through an
// identity provider that sent only an address — has none, so there has to be
// somewhere to supply one. It is also the only place a password can be changed
// by the person who owns it: reset-by-email exists, but making somebody lock
// themselves out to change a password they still know is a strange ritual.
//
// Two independent forms rather than one. They fail differently — a wrong
// current password is not a reason to discard a corrected surname — and a
// single Save submitting both would have to explain which half worked.
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { ToastHost } from '../../community/ui/toast';
import {
  SHARP_MOTION_BLURB,
  SHARPNESS_BLURB,
  useViewerSharpMotionSetting,
  useViewerSharpnessSetting,
} from '../../features/viewer-controls/viewerSharpness';
import { XRAY_AUTO_WINDOW_BLURB, useViewerXrayAutoWindowSetting } from '../../features/viewer-controls/xrayAutoWindow';
import {
  changePassword,
  currentSession,
  updateProfile,
  type SessionUser,
} from '../../auth/passwordSession';

/** Kept in step with the server's own minimum; it refuses anything shorter. */
const MIN_PASSWORD = 8;

export function ProfilePage(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: currentSession,
    staleTime: Infinity,
    retry: false,
  });

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-foreground">Your account</h1>
        <p className="text-sm text-muted-foreground">
          How you appear to colleagues, how you sign in, and how the viewer draws slides.
        </p>
      </div>
      {isLoading && <Skeleton className="h-40 w-full" />}
      {data && (
        <div className="space-y-6">
          <NameForm session={data} />
          {/* Only where there is a password to change. An account that signs in
              through an identity provider has none here, and the server says so
              rather than pretending — so the form is absent instead of being a
              form that always fails. */}
          {data.hasPassword !== false && <PasswordForm />}
          <ViewerCard />
          <XrayCard />
        </div>
      )}
      {/* Where a setting that could not be saved says so (viewer-controls accountChoice). */}
      <ToastHost />
    </div>
  );
}

const PRIORITIES = [
  { sharp: false, label: 'Speed' },
  { sharp: true, label: 'Sharpness' },
] as const;

/**
 * The viewer's loading priority, as community's Settings has it: Speed or
 * Sharpness (also in the viewer's bottom bar), and under Sharpness alone its
 * Sharpness in Motion, the costly one, a step further away (user, 2026-10-12).
 * Kept on the account.
 */
function ViewerCard(): JSX.Element {
  const [sharp, setSharp] = useViewerSharpnessSetting();
  const [motion, setMotion] = useViewerSharpMotionSetting();
  return (
    <Card title="Loading priority" blurb={SHARPNESS_BLURB}>
      <div role="radiogroup" aria-label="Loading priority" className="inline-flex gap-0.5 rounded-md border p-0.5">
        {PRIORITIES.map((p) => (
          <button
            key={p.label}
            type="button"
            role="radio"
            aria-checked={sharp === p.sharp}
            onClick={() => setSharp(p.sharp)}
            className={cn(
              'rounded px-3 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              sharp === p.sharp ? 'bg-accent font-medium text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>
      {sharp && (
        <div className="ml-1 mt-4 border-l-2 pl-3">
          <Switch checked={motion} onCheckedChange={setMotion} label="Sharpness in Motion" />
          <p className="mt-1 text-xs text-muted-foreground">{SHARP_MOTION_BLURB}</p>
        </div>
      )}
    </Card>
  );
}

/** X-rays open on the automatic window, as community's Settings has it (user, 2026-10-16). Kept on the account. */
function XrayCard(): JSX.Element {
  const [on, setOn] = useViewerXrayAutoWindowSetting();
  return (
    <Card title="X-ray auto window and level" blurb={XRAY_AUTO_WINDOW_BLURB}>
      <Switch checked={on} onCheckedChange={setOn} label="Open X-rays on the automatic window" />
    </Card>
  );
}

function Card({ title, blurb, children }: { title: string; blurb: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border p-4">
      <h2 className="text-sm font-medium text-foreground">{title}</h2>
      <p className="mb-4 text-xs text-muted-foreground">{blurb}</p>
      {children}
    </section>
  );
}

function NameForm({ session }: { session: SessionUser }): JSX.Element {
  const qc = useQueryClient();
  const [given, setGiven] = useState(session.givenName ?? '');
  const [family, setFamily] = useState(session.familyName ?? '');
  const [saved, setSaved] = useState(false);

  // Re-seeded when the session changes. The query is cached with
  // staleTime: Infinity, so a first render before it resolves would otherwise
  // leave the fields holding empty strings for good.
  useEffect(() => {
    setGiven(session.givenName ?? '');
    setFamily(session.familyName ?? '');
  }, [session.givenName, session.familyName]);

  const m = useMutation({
    mutationFn: () => updateProfile(given.trim(), family.trim()),
    onSuccess: () => {
      setSaved(true);
      // Everything showing a name reads the session — the avatar's initials,
      // the menu, the members list. Invalidating corrects all of them with one
      // fetch rather than patching each.
      void qc.invalidateQueries({ queryKey: ['auth', 'session'] });
    },
  });

  const unchanged =
    given.trim() === (session.givenName ?? '') && family.trim() === (session.familyName ?? '');

  return (
    <Card
      title="Name"
      blurb="Shown on workspaces you share, on invitations you send, and beside your annotations."
    >
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setSaved(false);
          if (given.trim() && family.trim()) m.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-foreground">First name</span>
            <Input
              value={given}
              onChange={(e) => {
                setGiven(e.target.value);
                setSaved(false);
              }}
              autoComplete="given-name"
              disabled={m.isPending}
            />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-foreground">Last name</span>
            <Input
              value={family}
              onChange={(e) => {
                setFamily(e.target.value);
                setSaved(false);
              }}
              autoComplete="family-name"
              disabled={m.isPending}
            />
          </label>
        </div>
        <div className="space-y-1.5">
          <span className="text-xs font-medium text-foreground">Email</span>
          {/* Shown, not editable. Changing the address somebody signs in with
              means proving the new one first, and a field that silently did not
              is worse than no field at all. */}
          <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {session.email ?? 'No address on this account'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="submit"
            size="sm"
            disabled={m.isPending || unchanged || !given.trim() || !family.trim()}
          >
            {m.isPending ? 'Saving…' : 'Save'}
          </Button>
          {saved && !m.isPending && !m.error && (
            <span role="status" className="text-xs text-primary">
              Saved
            </span>
          )}
          {m.error && (
            <span role="alert" className="text-xs text-destructive">
              {(m.error as Error).message}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

function PasswordForm(): JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [done, setDone] = useState(false);

  const m = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      setDone(true);
      setCurrent('');
      setNext('');
    },
  });

  return (
    <Card
      title="Password"
      blurb="Your current password is required, so a session left open on a shared machine cannot be used to take the account."
    >
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setDone(false);
          if (current && next) m.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-foreground">Current password</span>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              disabled={m.isPending}
            />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-foreground">New password</span>
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              disabled={m.isPending}
            />
          </label>
        </div>
        <p className="text-xs text-muted-foreground">
          At least {MIN_PASSWORD} characters, and not one you have used elsewhere.
        </p>
        <div className="flex items-center gap-3">
          <Button
            type="submit"
            size="sm"
            disabled={m.isPending || !current || next.length < MIN_PASSWORD}
          >
            {m.isPending ? 'Changing…' : 'Change password'}
          </Button>
          {done && !m.isPending && !m.error && (
            <span role="status" className="text-xs text-primary">
              Password changed
            </span>
          )}
          {m.error && (
            <span role="alert" className="text-xs text-destructive">
              {(m.error as Error).message}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}
